import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import type { TFunction } from "i18next";
import { formatFileSize } from "@/utils/fileUtils";
import { formatBilledDuration } from "@/utils/footageUsage";
import type { UsageEventDto } from "@/types";

function eventTitle(
  event: UsageEventDto,
  t: TFunction,
): { text: string; deleted: boolean } {
  if (event.videoFile) return { text: event.videoFile.filename, deleted: false };
  if (event.liveStream)
    return {
      text: event.liveStream.description || t("liveStreamFallback"),
      deleted: false,
    };
  return { text: t("deletedFootage"), deleted: true };
}

interface FootageTimelineItemProps {
  event: UsageEventDto;
  timeFormatter: Intl.DateTimeFormat;
}

/**
 * Accent colors per known event kind. Unknown future kinds fall back to a
 * neutral slate treatment instead of silently wearing the "streamed" look.
 */
const KIND_STYLES: Record<string, { dot: string; badge: string }> = {
  uploaded: { dot: "bg-indigo-500", badge: "bg-indigo-50 text-indigo-700" },
  streamed: { dot: "bg-amber-500", badge: "bg-[#fef3c7] text-[#92400e]" },
};

const KIND_FALLBACK = {
  dot: "bg-secondary-400",
  badge: "bg-secondary-100 text-secondary-600",
};

const KIND_LABEL_KEYS: Record<string, string> = {
  uploaded: "typeUploaded",
  streamed: "typeStreamed",
};

export default function FootageTimelineItem({
  event,
  timeFormatter,
}: FootageTimelineItemProps) {
  const { t } = useTranslation("footageUsage");
  const title = eventTitle(event, t);
  const styles = KIND_STYLES[event.type] ?? KIND_FALLBACK;
  // Known kinds get a translated label; unknown kinds fall back to their
  // raw kind name (i18next returns the last key when nothing is defined).
  const label = t([
    KIND_LABEL_KEYS[event.type] ?? `type_${event.type}`,
    event.type,
  ]);

  return (
    <div
      data-testid="ingest-event"
      data-type={event.type}
      className="relative mb-4 pl-8"
    >
      <div
        className={`absolute left-0 top-3 h-[15px] w-[15px] rounded-full border-2 border-white shadow-sm ${styles.dot}`}
      />
      <div className="rounded-lg border border-secondary-200 bg-white px-4 py-3 shadow-sm transition-shadow hover:shadow-md">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span
                className={`truncate text-sm font-semibold ${
                  title.deleted
                    ? "italic text-secondary-500"
                    : "text-secondary-900"
                }`}
              >
                {title.text}
              </span>
              <span
                className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${styles.badge}`}
              >
                {label}
              </span>
            </div>
            <div className="mt-0.5 flex flex-wrap items-center gap-x-2 text-xs text-secondary-500">
              <span>{timeFormatter.format(new Date(event.occurredAt))}</span>
              <span>·</span>
              <span className="font-mono font-medium tabular-nums text-secondary-700">
                {t("billed", {
                  duration: formatBilledDuration(event.seconds),
                })}
              </span>
              {event.videoFile?.fileSize != null && (
                <>
                  <span>·</span>
                  <span className="font-mono">
                    {formatFileSize(event.videoFile.fileSize)}
                  </span>
                </>
              )}
              {title.deleted && (
                <>
                  <span>·</span>
                  <span>{t("sourceRemoved")}</span>
                </>
              )}
            </div>
          </div>
          {event.videoFile?.sessionId && (
            <Link
              to={`/sessions/${event.videoFile.sessionId}`}
              className="whitespace-nowrap text-xs font-medium text-primary-600 hover:text-primary-700"
            >
              {t("viewSession")} ›
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}
